import { defineMessages } from 'react-intl';

/**
 * Every label the navigation shows: the items, the groups and their hub copy.
 *
 * The item ids are the old sidebar ids on purpose. The native apps read several of them
 * (`scripts/music24-native/i18n-keys.txt`), so a rename there would break their catalogue.
 */
export const smlNavigationMessages = defineMessages({
  home: { id: 'app.sml.nav.home', defaultMessage: 'Home' },
  watchlist: { id: 'app.sml.nav.watchlist', defaultMessage: 'Watchlist' },
  trends: { id: 'app.sml.sidebar.trends', defaultMessage: 'Trends' },
  artists: { id: 'app.sml.sidebar.artists', defaultMessage: 'Artists' },
  tracks: { id: 'app.sml.sidebar.tracks', defaultMessage: 'Tracks' },
  playlists: { id: 'app.sml.sidebar.playlists', defaultMessage: 'Playlists' },
  markets: { id: 'app.sml.sidebar.markets', defaultMessage: 'Markets' },
  browse: { id: 'app.sml.sidebar.browse', defaultMessage: 'Browse' },
  catalog: { id: 'app.sml.sidebar.catalog', defaultMessage: 'Catalog' },
  streaming: { id: 'app.sml.sidebar.streaming', defaultMessage: 'Streaming' },
  social: { id: 'app.sml.sidebar.social', defaultMessage: 'Social' },
  fans: { id: 'app.sml.sidebar.fans', defaultMessage: 'Fans' },
  // The old names. The command palette still finds Streaming by "Insights" and Social by
  // "Performance" (design your-intelligence h4). The native apps read these two ids too.
  insights: { id: 'app.sml.sidebar.insights', defaultMessage: 'Insights' },
  promotePerformance: { id: 'app.sml.sidebar.promote.performance', defaultMessage: 'Performance' },
  smartlinks: { id: 'app.sml.sidebar.smartlinks', defaultMessage: 'SmartLinks' },
  promoteReleases: { id: 'app.sml.sidebar.promote.releases', defaultMessage: 'Releases' },
  promoteStudio: { id: 'app.sml.sidebar.promote.studio', defaultMessage: 'Studio' },
  // Owner, 2026-10-11: Work and Composer are menu items again. The native apps name their Work
  // screen with the Work id too.
  promoteWork: { id: 'app.sml.sidebar.promote.work', defaultMessage: 'Work' },
  promoteAssets: { id: 'app.sml.sidebar.promote.assets', defaultMessage: 'Assets' },
  promoteComposer: { id: 'app.sml.sidebar.promote.composer', defaultMessage: 'Composer' },
  promotePosts: { id: 'app.sml.sidebar.promote.posts', defaultMessage: 'Posts' },
  promoteCalendar: { id: 'app.sml.sidebar.promote.calendar', defaultMessage: 'Calendar' },
  promoteChannels: { id: 'app.sml.sidebar.promote.channels', defaultMessage: 'Channels' },
  settings: { id: 'app.sml.sidebar.settings', defaultMessage: 'Settings' },
  outreachPitch: { id: 'app.sml.sidebar.outreach.pitch', defaultMessage: 'Pitch' },
  outreachFind: { id: 'app.sml.sidebar.outreach.find', defaultMessage: 'Find' },
  outreachCommunities: { id: 'app.sml.sidebar.outreach.communities', defaultMessage: 'Community spots' },
  outreachContacts: { id: 'app.sml.sidebar.outreach.contacts', defaultMessage: 'Contacts' },
  outreachInbox: { id: 'app.sml.sidebar.outreach.inbox', defaultMessage: 'Inbox' },
  // Navigation v2 (owner, 2026-10-23): the items that hold tabs. `insights` above is the old name
  // that the native apps read; the menu item has its own id.
  insightsItem: { id: 'app.sml.nav.insights', defaultMessage: 'Insights' },
  charts: { id: 'app.sml.nav.charts', defaultMessage: 'Charts' },
  connections: { id: 'app.sml.nav.connections', defaultMessage: 'Connections' },
});

/** The tab names of the pages with tabs that the menu labels do not already give (navigation v2). */
export const smlSectionTabMessages = defineMessages({
  postsBoard: { id: 'app.sml.posts.tab.board', defaultMessage: 'Board' },
  postsCalendar: { id: 'app.sml.posts.tab.calendar', defaultMessage: 'Calendar' },
  outreachCampaigns: { id: 'app.sml.outreach.tab.campaigns', defaultMessage: 'Campaigns' },
});

/**
 * The group names: the full name (sidebar, breadcrumb) and the tab name (mobile bottom bar).
 * Navigation v2 (owner, 2026-10-23): "Your music" and "Discover" replace "Your intelligence" and
 * "Public intelligence". The group ids stay `yours` and `public`.
 */
export const smlNavigationGroupMessages = defineMessages({
  publicName: { id: 'app.sml.nav.group.discover', defaultMessage: 'Discover' },
  yoursName: { id: 'app.sml.nav.group.music', defaultMessage: 'Your music' },
  promoteName: { id: 'app.sml.sidebar.section.promote', defaultMessage: 'Promote' },
  publicTab: { id: 'app.sml.nav.tab.discover', defaultMessage: 'Discover' },
  yoursTab: { id: 'app.sml.nav.tab.music', defaultMessage: 'Music' },
  promoteTab: { id: 'app.sml.sidebar.promote', defaultMessage: 'Promote' },
  outreachName: { id: 'app.sml.nav.group.outreach', defaultMessage: 'Outreach' },
  outreachTab: { id: 'app.sml.nav.tab.outreach', defaultMessage: 'Outreach' },
  outreachDescription: {
    id: 'app.sml.nav.group.outreach.description',
    defaultMessage: 'Pitch your release to playlists, press and radio, and read the replies.',
  },
  publicDescription: {
    id: 'app.sml.nav.group.public.description',
    defaultMessage: 'Playlist and market data across 10+ services.',
  },
  yoursDescription: {
    id: 'app.sml.nav.group.yours.descriptionStreaming',
    defaultMessage: 'Your releases, your streaming numbers and your social numbers.',
  },
  promoteDescription: {
    id: 'app.sml.nav.group.promote.description',
    defaultMessage: 'Tools to push a release: links, posts and reels.',
  },
});

/** The one-line tile text in the mobile hub sheets (navigation v2, design g 7b). */
export const smlNavigationDescriptionMessages = defineMessages({
  watchlist: { id: 'app.sml.nav.item.watchlist.description', defaultMessage: 'What you follow, read daily' },
  catalog: {
    id: 'app.sml.nav.item.catalog.descriptionSources',
    defaultMessage: 'Your releases and the sources you connect',
  },
  insights: {
    id: 'app.sml.nav.item.insights.description',
    defaultMessage: 'Streaming, social and your fans',
  },
  promoteReleases: {
    id: 'app.sml.nav.item.promoteReleases.description',
    defaultMessage: 'Your songs, each with its link, kit and plan.',
  },
  promoteStudio: { id: 'app.sml.nav.item.promoteStudio.description', defaultMessage: 'Make covers, clips and reels.' },
  promotePosts: { id: 'app.sml.nav.item.promotePosts.description', defaultMessage: 'Plan and publish your posts.' },
  promoteChannels: {
    id: 'app.sml.nav.item.promoteChannels.description',
    defaultMessage: 'Connect your social accounts, so posts go out by themselves.',
  },
  smartlinks: { id: 'app.sml.nav.item.smartlinks.description', defaultMessage: 'One link to every store.' },
  outreachPitch: {
    id: 'app.sml.nav.item.outreachPitch.description',
    defaultMessage: 'Pitch a release, see what worked',
  },
  outreachInbox: { id: 'app.sml.nav.item.outreachInbox.description', defaultMessage: 'Replies to your pitches' },
  charts: {
    id: 'app.sml.nav.item.charts.description',
    defaultMessage: 'What moves on playlists across 10+ services',
  },
});

/**
 * Navigation v1 copy that the web shows no more. The native apps still read these ids
 * (`scripts/music24-native/i18n-keys.txt`), so they stay in the catalogue until the native apps
 * move to navigation v2.
 */
export const smlNavigationLegacyMessages = defineMessages({
  // Navigation v2 removed the tagline under the sidebar logo.
  brandTag: { id: 'app.sml.sidebar.brandTag', defaultMessage: 'Music Marketing' },
  // Navigation v2: "Plans and billing" in the account sheet replaces this button.
  seePlans: { id: 'app.sml.nav.seePlans', defaultMessage: 'See plans' },
  publicName: { id: 'app.sml.nav.group.public', defaultMessage: 'Public intelligence' },
  yoursName: { id: 'app.sml.nav.group.yours', defaultMessage: 'Your intelligence' },
  publicTab: { id: 'app.sml.nav.tab.public', defaultMessage: 'Public' },
  yoursTab: { id: 'app.sml.nav.tab.yours', defaultMessage: 'Yours' },
  trends: { id: 'app.sml.nav.item.trends.description', defaultMessage: 'What moved yesterday' },
  artists: { id: 'app.sml.nav.item.artists.description', defaultMessage: 'Ranked by playlist reach' },
  tracks: { id: 'app.sml.nav.item.tracks.description', defaultMessage: 'Ranked by playlist momentum' },
  playlists: { id: 'app.sml.nav.item.playlists.description', defaultMessage: 'Editorial and algorithmic' },
  markets: { id: 'app.sml.nav.item.markets.description', defaultMessage: 'Where the music travels' },
  browse: { id: 'app.sml.nav.item.browse.description', defaultMessage: 'Search 10+ services' },
  streaming: {
    id: 'app.sml.nav.item.streaming.description',
    defaultMessage: 'Streams, stores, countries and earnings',
  },
  social: {
    id: 'app.sml.nav.item.social.description',
    defaultMessage: 'Followers and the results of your posts',
  },
  outreachFind: {
    id: 'app.sml.nav.item.outreachFind.description',
    defaultMessage: 'Playlists, press and radio that fit you',
  },
  outreachCommunities: {
    id: 'app.sml.nav.item.outreachCommunities.description',
    defaultMessage: 'Subreddits where you may post yourself',
  },
  outreachContacts: {
    id: 'app.sml.nav.item.outreachContacts.description',
    defaultMessage: 'Everyone you found or imported',
  },
});

/** The chrome around the items: landmarks, buttons, badges and the hub captions. */
export const smlNavigationChromeMessages = defineMessages({
  mainNav: { id: 'app.sml.nav.landmark.main', defaultMessage: 'Main' },
  groupsNav: { id: 'app.sml.nav.landmark.groups', defaultMessage: 'Sections' },
  collapse: { id: 'app.sml.nav.collapse', defaultMessage: 'Collapse the sidebar' },
  expand: { id: 'app.sml.nav.expand', defaultMessage: 'Expand the sidebar' },
  search: { id: 'app.sml.nav.search', defaultMessage: 'Search' },
  // The plans page (/app/plans) keeps the title of its own page head.
  plans: { id: 'app.sml.plans.page.title', defaultMessage: 'Plans' },
  account: { id: 'app.sml.nav.account', defaultMessage: 'Account' },
  close: { id: 'app.sml.nav.close', defaultMessage: 'Close' },
  back: { id: 'app.sml.nav.back', defaultMessage: 'Back to {destination}' },
  more: { id: 'app.sml.nav.more', defaultMessage: 'More' },
  help: { id: 'app.sml.nav.help', defaultMessage: 'Help and support' },
  failedPosts: {
    id: 'app.sml.nav.badge.failedPosts',
    defaultMessage: '{count, plural, one {# post failed to publish} other {# posts failed to publish}}',
  },
  // The account card and the Connections row of the account menu read it (navigation v2).
  channelsReconnect: {
    id: 'app.sml.nav.badge.channelsReconnect',
    defaultMessage: '{count, plural, one {# account needs a new sign-in} other {# accounts need a new sign-in}}',
  },
  outreachInbox: {
    id: 'app.sml.nav.badge.outreachInbox',
    defaultMessage: '{count, plural, one {# reply waits for you} other {# replies wait for you}}',
  },
  catalogSetup: {
    id: 'app.sml.nav.badge.catalogSetupSources',
    defaultMessage: 'Catalog needs setup: a release, a streaming source or an artist',
  },
  catalogSetupRelease: {
    id: 'app.sml.nav.badge.catalogSetup.release',
    defaultMessage: 'Catalog: add your first song',
  },
  catalogSetupArtist: {
    id: 'app.sml.nav.badge.catalogSetup.artist',
    defaultMessage: 'Catalog: choose the artist of your streaming login',
  },
  catalogReconnect: {
    id: 'app.sml.nav.badge.catalogReconnect',
    defaultMessage:
      '{count, plural, one {# streaming source needs Reconnect} other {# streaming sources need Reconnect}}',
  },
  usagePortalHistory: {
    id: 'app.sml.nav.usage.portalHistory',
    defaultMessage: '{days, plural, one {# day of history} other {# days of history}} on {plan}',
  },
  usagePages: { id: 'app.sml.nav.usage.pages', defaultMessage: '{used} of {limit} published pages' },
  usageScheduledPosts: {
    id: 'app.sml.nav.usage.scheduledPosts',
    defaultMessage: '{limit, plural, one {# post a month} other {# posts a month}} on {plan}',
  },
  usageVideoCredits: {
    id: 'app.sml.nav.usage.videoCredits',
    defaultMessage: '{limit, plural, one {# video credit a month} other {# video credits a month}} on {plan}',
  },
  usageSocialAccounts: {
    id: 'app.sml.nav.usage.socialAccounts',
    defaultMessage: '{limit, plural, one {# social account} other {# social accounts}} on {plan}',
  },
});
